import { useEffect, useLayoutEffect, useState } from "react";
import type { CadenceAgent, CadenceCollaborationInteraction } from "./types";

export const COLLABORATION_ACTIVE_MS = 8000;
export const COLLABORATION_RECENT_MS = 3000;
export const COLLABORATION_IDLE_MS = 500;

const MAX_INITIAL_INTERACTIONS = 4;
const MAX_QUEUED_INTERACTIONS = 24;
const MAX_SEEN_INTERACTIONS = 64;
const EMPTY_AGENTS: CadenceAgent[] = [];

type PlaybackPhase = "active" | "recent" | "idle";

interface PlaybackState {
  sessionKey: string;
  enabled: boolean;
  snapshots: Map<string, CadenceCollaborationInteraction>;
  agentSnapshots: Map<string, CadenceAgent[]>;
  seenIds: string[];
  queue: string[];
  currentId: string | null;
  phase: PlaybackPhase | null;
  phaseStartedAt: number;
}

interface UseCollaborationPlaybackOptions {
  interactions: CadenceCollaborationInteraction[];
  agents?: CadenceAgent[];
  sessionKey: string;
  enabled?: boolean;
  activeMs?: number;
  recentMs?: number;
  idleMs?: number;
}

function sortedInteractions(
  interactions: CadenceCollaborationInteraction[],
): CadenceCollaborationInteraction[] {
  const snapshots = new Map<string, CadenceCollaborationInteraction>();
  interactions.forEach((interaction) =>
    snapshots.set(interaction.id, interaction),
  );
  return [...snapshots.values()]
    .sort(
      (left, right) => left.seq - right.seq || left.id.localeCompare(right.id),
    )
    .slice(-MAX_SEEN_INTERACTIONS);
}

function sortedQueue(
  interactionIds: string[],
  snapshots: Map<string, CadenceCollaborationInteraction>,
): string[] {
  return [...new Set(interactionIds)]
    .sort((leftId, rightId) => {
      const left = snapshots.get(leftId);
      const right = snapshots.get(rightId);
      return (
        (left?.seq ?? 0) - (right?.seq ?? 0) || leftId.localeCompare(rightId)
      );
    })
    .slice(-MAX_QUEUED_INTERACTIONS);
}

function startNext(state: PlaybackState): PlaybackState {
  const [currentId, ...queue] = state.queue;
  if (!currentId) {
    return {
      ...state,
      currentId: null,
      phase: null,
      phaseStartedAt: Date.now(),
    };
  }
  return {
    ...state,
    queue,
    currentId,
    phase:
      state.snapshots.get(currentId)?.completed === true ? "recent" : "active",
    phaseStartedAt: Date.now(),
  };
}

function initialState(
  sessionKey: string,
  interactions: CadenceCollaborationInteraction[],
  agents: CadenceAgent[],
  enabled: boolean,
): PlaybackState {
  const normalized = sortedInteractions(interactions);
  const initialInteractions = normalized.slice(-MAX_INITIAL_INTERACTIONS);
  const snapshots = new Map(
    normalized.map((interaction) => [interaction.id, interaction]),
  );
  const agentSnapshots = new Map(
    initialInteractions.map((interaction) => [interaction.id, agents]),
  );
  const base: PlaybackState = {
    sessionKey,
    enabled,
    snapshots,
    agentSnapshots,
    seenIds: normalized
      .slice(-MAX_SEEN_INTERACTIONS)
      .map((interaction) => interaction.id),
    queue: initialInteractions.map((interaction) => interaction.id),
    currentId: null,
    phase: null,
    phaseStartedAt: Date.now(),
  };
  return enabled ? startNext(base) : base;
}

function ingest(
  state: PlaybackState,
  interactions: CadenceCollaborationInteraction[],
  agents: CadenceAgent[],
): PlaybackState {
  const snapshots = new Map(state.snapshots);
  const agentSnapshots = new Map(state.agentSnapshots);
  const seenIds = [...state.seenIds];
  const seen = new Set(seenIds);
  const newIds: string[] = [];

  sortedInteractions(interactions).forEach((interaction) => {
    const existing = snapshots.get(interaction.id);
    snapshots.set(
      interaction.id,
      existing && sameInteraction(existing, interaction)
        ? existing
        : interaction,
    );
    agentSnapshots.set(interaction.id, agents);
    if (!seen.has(interaction.id)) {
      seen.add(interaction.id);
      seenIds.push(interaction.id);
      newIds.push(interaction.id);
    }
  });

  const next = {
    ...state,
    snapshots,
    agentSnapshots,
    seenIds: seenIds.slice(-MAX_SEEN_INTERACTIONS),
    queue: sortedQueue([...state.queue, ...newIds], snapshots),
  };
  const currentInteraction = next.currentId
    ? snapshots.get(next.currentId)
    : undefined;
  if (
    state.phase === "active" &&
    state.currentId === next.currentId &&
    currentInteraction?.completed === true &&
    state.snapshots.get(state.currentId ?? "")?.completed !== true
  ) {
    return {
      ...next,
      phase: "recent",
      phaseStartedAt: Date.now(),
    };
  }
  if (
    !next.enabled ||
    next.currentId ||
    next.phase === "idle" ||
    next.queue.length === 0
  ) {
    return next;
  }
  return startNext(next);
}

function syncState(
  state: PlaybackState,
  interactions: CadenceCollaborationInteraction[],
  agents: CadenceAgent[],
  enabled: boolean,
): PlaybackState {
  const next = ingest(state, interactions, agents);
  if (state.enabled === enabled) return next;
  if (!enabled) return { ...next, enabled: false };
  const resumed = { ...next, enabled: true, phaseStartedAt: Date.now() };
  if (resumed.currentId || resumed.phase === "idle") return resumed;
  return startNext(resumed);
}

function sameStringArray(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function sameInteraction(
  left: CadenceCollaborationInteraction,
  right: CadenceCollaborationInteraction,
): boolean {
  return (
    left.id === right.id &&
    left.kind === right.kind &&
    left.protocolSource === right.protocolSource &&
    left.sourceAgentId === right.sourceAgentId &&
    sameStringArray(left.targetAgentIds, right.targetAgentIds) &&
    left.preview === right.preview &&
    left.messageId === right.messageId &&
    left.needReply === right.needReply &&
    left.seq === right.seq &&
    left.updatedSeq === right.updatedSeq &&
    left.completed === right.completed
  );
}

function sameMap<K, V>(left: Map<K, V>, right: Map<K, V>): boolean {
  if (left.size !== right.size) return false;
  for (const [key, value] of left) {
    if (right.get(key) !== value) return false;
  }
  return true;
}

function samePlaybackState(left: PlaybackState, right: PlaybackState): boolean {
  return (
    left.sessionKey === right.sessionKey &&
    left.enabled === right.enabled &&
    left.currentId === right.currentId &&
    left.phase === right.phase &&
    left.phaseStartedAt === right.phaseStartedAt &&
    sameStringArray(left.seenIds, right.seenIds) &&
    sameStringArray(left.queue, right.queue) &&
    sameMap(left.snapshots, right.snapshots) &&
    sameMap(left.agentSnapshots, right.agentSnapshots)
  );
}

export function useCollaborationPlayback({
  interactions,
  agents = EMPTY_AGENTS,
  sessionKey,
  enabled = true,
  activeMs = COLLABORATION_ACTIVE_MS,
  recentMs = COLLABORATION_RECENT_MS,
  idleMs = COLLABORATION_IDLE_MS,
}: UseCollaborationPlaybackOptions): {
  interaction: CadenceCollaborationInteraction | null;
  agents: CadenceAgent[] | null;
  interactionState: "active" | "recent" | null;
  queuedCount: number;
} {
  const [state, setState] = useState(() =>
    initialState(sessionKey, interactions, agents, enabled),
  );
  useLayoutEffect(() => {
    setState((current) => {
      const next =
        current.sessionKey === sessionKey
          ? syncState(current, interactions, agents, enabled)
          : initialState(sessionKey, interactions, agents, enabled);
      return samePlaybackState(current, next) ? current : next;
    });
  }, [agents, enabled, interactions, sessionKey]);

  useEffect(() => {
    if (
      !enabled ||
      !state.enabled ||
      !state.phase ||
      state.sessionKey !== sessionKey
    ) {
      return undefined;
    }
    const elapsed = Date.now() - state.phaseStartedAt;
    const duration =
      state.phase === "active"
        ? activeMs
        : state.phase === "recent"
          ? recentMs
          : idleMs;
    const timer = window.setTimeout(
      () => {
        setState((current) => {
          if (
            current.sessionKey !== sessionKey ||
            current.currentId !== state.currentId ||
            current.phase !== state.phase
          ) {
            return current;
          }
          if (current.phase === "active") {
            return { ...current, phase: "recent", phaseStartedAt: Date.now() };
          }
          if (current.phase === "recent") {
            return {
              ...current,
              currentId: null,
              phase: "idle",
              phaseStartedAt: Date.now(),
            };
          }
          return startNext(current);
        });
      },
      Math.max(0, duration - elapsed),
    );
    return () => window.clearTimeout(timer);
  }, [
    activeMs,
    enabled,
    idleMs,
    recentMs,
    sessionKey,
    state.currentId,
    state.enabled,
    state.phase,
    state.phaseStartedAt,
    state.sessionKey,
  ]);

  const visiblePhase =
    state.phase === "active" || state.phase === "recent" ? state.phase : null;
  if (
    !enabled ||
    !state.enabled ||
    state.sessionKey !== sessionKey ||
    !visiblePhase
  ) {
    return {
      interaction: null,
      agents: null,
      interactionState: null,
      queuedCount: state.sessionKey === sessionKey ? state.queue.length : 0,
    };
  }
  return {
    interaction: state.currentId
      ? (state.snapshots.get(state.currentId) ?? null)
      : null,
    agents: state.currentId
      ? (state.agentSnapshots.get(state.currentId) ?? null)
      : null,
    interactionState: visiblePhase,
    queuedCount: state.queue.length,
  };
}
